import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useSession } from "@/context/Session";
import { OrgCard, InviteCard, RolesCard } from "@/components/OrgSettings";
import { X } from "lucide-react";
import { t } from "@/i18n";
import { TrainingBar, useTrainingPlans } from "@/pages/Training";

function OiManagerCard() {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/org/oi-manager").then((r) => setD(r.data)); }, [api]);
  if (!d) return null;
  const change = async (user_id) => {
    try { const { data } = await api.put("/org/oi-manager", { user_id }); setD({ ...d, current: data }); toast.success(t("Saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card stack-sm" data-testid="oi-manager-card">
      <p className="mono-label">{t("OI base manager")}</p>
      <p className="text-sm muted">{t("Questions the OI base can't answer go to this person.")}</p>
      <select className="field max-w-sm" value={d.current.user_id} onChange={(e) => change(e.target.value)} data-testid="oi-manager-select">
        {d.candidates.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}{c.title ? `, ${c.title}` : ""}</option>)}
      </select>
    </div>
  );
}

const LABEL = { admin: "Owner / Admin", manager: "Manager", apprentice: "Apprentice" };

export default function Team() {
  const api = useApi();
  const { me } = useSession();
  const [data, setData] = useState(null);
  const [plans] = useTrainingPlans(true);
  const load = useCallback(() => api.get("/users").then((r) => setData(r.data)), [api]);
  useEffect(() => { load(); }, [load]);

  const toggleRole = async (u, rid) => {
    const next = u.role_ids.includes(rid) ? u.role_ids.filter((x) => x !== rid) : [...u.role_ids, rid];
    if (!next.length) return toast.error(t("Pick at least one role"));
    try { await api.patch(`/users/${u.user_id}/roles`, { role_ids: next }); toast.success(t("Role updated")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(t("Remove {name} from your organization?").replace("{name}", u.nickname || u.name || u.email))) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success(t("Removed")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const clearPreset = async (role) => { await api.delete(`/ui/presets/${role}`); toast.success(t("Cleared shared look for {role}", { role: t(LABEL[role]) })); };

  if (!data) return <p className="page faint">{t("Loading team…")}</p>;
  return (
    <div className="page stack">
      <div className="rise">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("Team")}</h1>
        <Link to="/billing" className="btn btn-ghost btn-sm mt-3" data-testid="team-billing-link">{t("Billing")}</Link>
      </div>
      <OrgCard />
      <InviteCard roles={data.org_roles} onChange={load} />
      <div className="card sm:overflow-x-auto" data-testid="team-table">
        <table className="w-full text-sm block sm:table">
          <thead className="hidden sm:table-header-group"><tr className="text-left mono-label"><th className="py-2">{t("Person")}</th><th>{t("Joined")}</th><th>{t("Role")}</th><th /></tr></thead>
          <tbody className="block sm:table-row-group">
            {data.users.map((u) => (
              <tr key={u.user_id} className="relative block sm:table-row border-t border-[var(--border-color)] first:border-t-0 sm:first:border-t py-2 sm:py-0" data-testid={`team-row-${u.user_id}`}>
                <td className="block sm:table-cell py-1 sm:py-3 pr-8 sm:pr-0"><p className="font-semibold" data-testid={`team-name-${u.user_id}`}>{u.nickname || u.name || u.email || u.user_id}{u.role_names?.length > 0 && <span className="font-normal faint">, {u.role_names.join(", ")}</span>}</p>{u.nickname && u.name && <p className="text-xs faint">{u.name}</p>}<p className="text-xs faint">{u.email}</p></td>
                <td className="hidden sm:table-cell faint">{fmtDate(u.created_at)}</td>
                <td className="block sm:table-cell">
                  <div className="flex flex-wrap gap-1.5 py-2" data-testid={`team-roles-${u.user_id}`}>
                    {data.org_roles.map((r) => {
                      const on = u.role_ids.includes(r.id);
                      return <button key={r.id} onClick={() => toggleRole(u, r.id)} aria-pressed={on} className={`chip !py-1 transition-colors ${on ? "chip-accent" : "chip-muted opacity-60 hover:opacity-100"}`} data-testid={`team-role-${u.user_id}-${r.id}`}>{r.name}</button>;
                    })}
                  </div>
                </td>
                <td className="absolute top-2 right-0 sm:static sm:table-cell text-right">{u.user_id !== me.user_id && <button className="faint hover:text-[var(--status-error)] p-1" onClick={() => remove(u)} title={t("Remove")} data-testid={`team-remove-${u.user_id}`}><X size={16} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OiManagerCard />
      {plans.length > 0 && <div className="card stack" data-testid="team-training">
        <p className="mono-label">{t("Training")}</p>
        {plans.map((p) => <TrainingBar key={p.user_id} plan={p} />)}
      </div>}
      <RolesCard onChange={load} />
      <div className="card stack-sm">
        <p className="mono-label">{t("Shared looks")}</p>
        <div className="flex flex-wrap gap-2">
          {data.roles.map((r) => <button key={r} className="btn btn-ghost btn-sm" onClick={() => clearPreset(r)} data-testid={`team-clear-preset-${r}`}>{t("Clear {role} preset", { role: t(LABEL[r]) })}</button>)}
        </div>
      </div>
    </div>
  );
}
